'use client'

import { Title, Text, Stack, Grid, GridCol, Center, Divider, Button } from '@mantine/core'
import { IconCompass, IconRocket, IconTrendingUp, IconUserEdit, IconUsers, IconStar } from '@tabler/icons-react'
import Link from 'next/link'
import styles from './ProcessSection.module.css'

const processContent = {
  mentee: {
    iconBackgroundColor: 'var(--mantine-color-marigold-1)',
    steps: [
      {
        icon: IconCompass,
        title: 'Find Your Match',
        description: 'Explore accomplished professionals across diverse industries. Each mentor is vetted and ready to accelerate your career.'
      },
      {
        icon: IconRocket,
        title: 'Begin Your Journey',
        description: 'Apply to work with mentors who align with your aspirations. Get accepted and start meaningful conversations addressing your challenges.'
      },
      {
        icon: IconTrendingUp,
        title: 'Level Up',
        description: 'Transform your skills with expert guidance and actionable feedback. Build confidence, expand your network, and accelerate professionally.'
      }
    ]
  },
  mentor: {
    iconBackgroundColor: 'var(--mantine-color-pistachio-1)',
    steps: [
      {
        icon: IconUserEdit,
        title: 'Create Your Mentor Profile',
        description: 'Build your mentor profile showcasing your expertise and experience. Get approved to join our exclusive community and start receiving mentorship requests.'
      },
      {
        icon: IconUsers,
        title: 'Select and Meet',
        description: 'Review requests from mentees seeking your expertise. Choose those you can help most and begin scheduling meaningful mentorship sessions.'
      },
      {
        icon: IconStar,
        title: 'Make an Impact',
        description: 'Shape the next generation of professionals while expanding your network. Develop your coaching skills, gain fresh perspectives, and build meaningful relationships that last.'
      }
    ]
  }
}

export default function ProcessSection() {
  return (
    <Stack gap={80} style={{ textAlign: 'center' }}>
      {/* Mentor Section */}
      <Stack gap="xl">
        <Title order={2} size="h1" className="gradient-emerald">
          Interested in mentoring?
        </Title>

        <Grid gutter="xl" pt="xl">
          {processContent.mentor.steps.map((step, index) => (
            <GridCol key={index} span={{ base: 12, sm: 4 }}>
              <Stack gap="md" align="center">
                <Center
                  style={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    backgroundColor: processContent.mentor.iconBackgroundColor,
                  }}
                >
                  <step.icon size={32} className="gradient-icon" />
                </Center>

                <Title order={3} c="navy.8">
                  {step.title}
                </Title>

                <Text size="lg" c="gray.7" style={{ maxWidth: '350px', lineHeight: 1.7 }}>
                  {step.description}
                </Text>
              </Stack>
            </GridCol>
          ))}
        </Grid>

        <Center mt="xl">
          <Link href="/become-a-mentor" style={{ textDecoration: 'none' }}>
            <Button size="lg" variant="gradient" gradient={{ from: 'emerald', to: 'teal', deg: 90 }}>
              Become a Mentor
            </Button>
          </Link>
        </Center>
      </Stack>

      {/* Section Divider */}
      <Divider
        size="sm"
        w="60%"
        mx="auto"
        color="emerald.4"
        styles={{
          root: { maxWidth: '600px' }
        }}
      />

      {/* Mentee Section */}
      <Stack gap="xl">
        <Title order={2} size="h1" className="gradient-emerald">
          Looking for a mentor?
        </Title>

        <Grid gutter="xl" pt="xl">
          {processContent.mentee.steps.map((step, index) => (
            <GridCol key={index} span={{ base: 12, sm: 4 }}>
              <Stack gap="md" align="center">
                <Center
                  style={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    backgroundColor: processContent.mentee.iconBackgroundColor,
                  }}
                >
                  <step.icon size={32} className="gradient-icon" />
                </Center>

                <Title order={3} c="navy.8">
                  {step.title}
                </Title>

                <Text size="lg" c="gray.7" style={{ maxWidth: '350px', lineHeight: 1.7 }}>
                  {step.description}
                </Text>
              </Stack>
            </GridCol>
          ))}
        </Grid>

        <Center mt="xl">
          <Link href="/mentors" style={{ textDecoration: 'none' }}>
            <Button size="lg" variant="gradient" gradient={{ from: 'marigold', to: 'orange', deg: 90 }}>
              Browse Mentors
            </Button>
          </Link>
        </Center>
      </Stack>
    </Stack>
  )
}